import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categoryGroups, categoryTabs } from '@/mocks/categoryData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(slug || 'all');
  const [isChanging, setIsChanging] = useState(false);

  const currentCategory = categoryGroups.find((g) => g.id === activeTab) || categoryGroups[0];

  const handleTabChange = (tabId: string) => {
    if (tabId === activeTab) return;
    setIsChanging(true);
    setActiveTab(tabId);
    navigate(`/category/${tabId}`, { replace: true });
    window.scrollTo({ top: 0, behavior: 'instant' });
    setTimeout(() => setIsChanging(false), 300);
  };

  const breadcrumbItems = [
    { label: '分类浏览', path: '/category/all' },
    { label: currentCategory.name },
  ];

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <section className="w-full py-8 md:py-12 bg-background-50">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              {currentCategory.name}
            </h1>
            <p className="text-foreground-600 text-sm md:text-base mb-8">
              {currentCategory.description}
            </p>

            <div className="flex items-center overflow-x-auto pb-2 mb-8 gap-1.5">
              {categoryTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeTab === tab.id
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${tab.icon} mr-1.5 text-xs`}></i>
                  {tab.name}
                </button>
              ))}
            </div>

            <div className={`transition-opacity duration-300 ${isChanging ? 'opacity-50' : 'opacity-100'}`}>
              {currentCategory.articles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {currentCategory.articles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer flex flex-col"
                    >
                      <div className="relative h-52 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5 flex flex-col flex-1">
                        <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-foreground-600 text-sm leading-relaxed mb-4 line-clamp-2 flex-1">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-foreground-500 pt-3 border-t border-background-200/60">
                          <span>{article.date}</span>
                          <span className="flex items-center">
                            <i className="ri-time-line mr-1"></i>
                            {article.readTime}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                    <i className="ri-inbox-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-600">该分类下暂无文章</p>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}